import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  InputAdornment,
  IconButton,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import AuthContext from '../contexts/AuthContext';
import { usePractice } from '../contexts/PracticeContext';
import UsersTabContent from '../components/OrganizationUsers/UsersTabContent';
import InvitationsTabContent from '../components/OrganizationUsers/InvitationsTabContent';

export default function OrgsUsers() {
  const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  // Invitations list state
  const [invSearchText, setInvSearchText] = useState('');
  const [invStatusFilter, setInvStatusFilter] = useState('');
  const [invitationsList, setInvitationsList] = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(false);
  const [invitesErrorList, setInvitesErrorList] = useState(null);
  const { selectedPractice } = usePractice();
  const { tokens } = useContext(AuthContext);
  // Tab state: 0 = Users, 1 = Invitations based on URL param
  const { tab } = useParams();
  const navigate = useNavigate();
  // Determine initial tab from URL: 'invitations' maps to index 1, else index 0
  const initialTab = tab === 'invitations' ? 1 : 0;
  const [tabIndex, setTabIndex] = useState(initialTab);
  // Sync state when URL param changes
  useEffect(() => {
    setTabIndex(tab === 'invitations' ? 1 : 0);
  }, [tab]);
  // Invitation dialog state
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('');
  const [inviteError, setInviteError] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
    // Update URL to allow linking to a specific tab
    if (newValue === 0) {
      navigate('/orgs-users');
    } else if (newValue === 1) {
      navigate('/orgs-users/invitations');
    }
  };
  const roleOptions = [];
  if (selectedPractice?.is_practice) roleOptions.push('Provider');
  if (selectedPractice?.is_collection_site) roleOptions.push('Phlebotomist');

  const handleInviteSubmit = () => {
    if (!inviteEmail) {
      setInviteError('Email is required');
      return;
    }
    setInviteError('');
    setInviteLoading(true);
    const payload = {
      email: inviteEmail,
      organization_id: selectedPractice.id,
      role: inviteRole,
    };
    fetch(`${apiBase}/invitation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${tokens.id_token}`
      },
      body: JSON.stringify(payload),
    })
      .then(res => {
        if (!res.ok) return res.json().then(data => Promise.reject(data.detail || 'Failed to send invite'));
        return res.json();
      })
      .then(() => {
        setInviteLoading(false);
        setInviteDialogOpen(false);
        setInviteEmail('');
      })
      .catch(err => {
        setInviteLoading(false);
        setInviteError(typeof err === 'string' ? err : (err.detail || JSON.stringify(err)));
      });
  };
  // Fetch invitations when on Invitations tab or filters change
  useEffect(() => {
    if (tabIndex !== 1) return;
    if (!tokens?.id_token || !selectedPractice?.id) return;
    setInvitesLoading(true);
    const params = new URLSearchParams();
    params.append('organization_id', selectedPractice.id);
    if (invSearchText) params.append('search_text', invSearchText);
    if (invStatusFilter) params.append('status', invStatusFilter);
    fetch(`${apiBase}/invitations?${params.toString()}`, {
      headers: { Authorization: `Bearer ${tokens.id_token}` }
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to load invitations');
        return res.json();
      })
      .then(data => {
        setInvitationsList(data.items || []);
        setInvitesErrorList(null);
      })
      .catch(err => {
        console.error(err);
        setInvitesErrorList(err.message || 'Error loading invitations');
      })
      .finally(() => setInvitesLoading(false));
  }, [tabIndex, invSearchText, invStatusFilter, selectedPractice, tokens, apiBase]);

  // Load users for the selected organization
  useEffect(() => {
    if (!tokens?.id_token || !selectedPractice?.id) return;
    setLoading(true);
    const params = new URLSearchParams();
    if (searchText) params.append('search_text', searchText);
    if (selectedRole) params.append('role', selectedRole);
    params.append('skip', page * rowsPerPage);
    params.append('limit', rowsPerPage);
    fetch(`${apiBase}/organizations/${selectedPractice.id}/users?${params.toString()}`, {
      headers: { Authorization: `Bearer ${tokens.id_token}` }
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch users');
        return res.json();
      })
      .then(data => {
        setUsers(data.items || []);
        setTotal(data.total || 0);
        setError(null);
      })
      .catch(err => {
        console.error('Error fetching users:', err);
        setError(err.message || 'Failed to fetch users');
      })
      .finally(() => setLoading(false));
  }, [tokens, selectedPractice, page, rowsPerPage, searchText, selectedRole, apiBase]);

  // Debounce search and refresh when searchText, selectedRole, page, rowsPerPage change

  // Re-fetch when dependencies change (handled in the effect above)

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
    setPage(0); // Reset to first page when search changes
  };

  const handleRoleChange = (event) => {
    setSelectedRole(event.target.value);
    setPage(0); // Reset to first page when role filter changes
  };

  if (!selectedPractice) {
    return (
      <Container>
        <Typography variant="h5" gutterBottom>
          Please select an organization to view its users
        </Typography>
      </Container>
    );
  }

  if (loading && !users.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container disableGutters>
        <Typography color="error" gutterBottom>
          {error}
        </Typography>
      </Container>
    );
  }

  return (
    <Container disableGutters sx={{ mt: 2 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Organization Users
      </Typography>
      <Tabs value={tabIndex} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="Users" />
        <Tab label="Invitations" />
      </Tabs>
      {tabIndex === 0 && (
        <UsersTabContent organizationId={selectedPractice.id} />
      )}
      {tabIndex === 1 && (
        <InvitationsTabContent organizationId={selectedPractice.id} />
      )}
    </Container>
  );
} 